import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import EventImage from "@/components/EventImage";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_META } from "@/lib/categories";
import { SABBAT_INFO } from "@/lib/sabbats";
import SabbatBadge from "@/components/SabbatBadge";
import Countdown from "@/components/Countdown";
import AttendanceButtons from "@/components/AttendanceButtons";
import EventPresence from "@/components/EventPresence";
import EventDiscussion from "@/components/community/EventDiscussion";
import FeaturedPros from "@/components/FeaturedPros";
import ConcertShare from "@/components/ConcertShare";
import ReportButton from "@/components/ReportButton";
import CampfireRelated from "@/components/CampfireRelated";
import { CampfireTopBanner } from "@/components/CampfireRelated";
import { API } from "@/lib/api";
import { formatDateRange, formatTime, buildGoogleCalUrl } from "@/lib/format";
import { CATEGORY_LABELS } from "@/i18n/translations";
import { MapPin, CalendarDays, User2, Ticket, Share2, Heart, CalendarPlus, ExternalLink, ArrowLeft, Clock, Star, Sparkles, Timer, Trash2 } from "lucide-react";

const pin = L.divIcon({ className: "", html: '<div class="pagan-marker promoted"></div>', iconSize: [22, 22], iconAnchor: [11, 22] });

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t, tCategory, tCountry } = useLang();
  const { user } = useAuth();
  const [fav, setFav] = useState(false);

  const { data: event, isLoading } = useQuery({ queryKey: ["event", slug], queryFn: async () => (await api.get(`/events/${slug}`)).data });
  const { data: related = [] } = useQuery({ queryKey: ["related", slug], queryFn: async () => (await api.get(`/events/${slug}/related`)).data, enabled: !!event });
  const { data: favIds = [] } = useQuery({ queryKey: ["favIds"], queryFn: async () => (await api.get("/favorites/ids")).data, enabled: !!user });

  useEffect(() => { setFav(favIds.includes(slug)); }, [favIds, slug]);

  const toggleFav = async () => {
    if (!user) { toast.error(t("submit_login")); return; }
    try {
      if (fav) { await api.delete(`/favorites/${slug}`); setFav(false); }
      else { await api.post(`/favorites/${slug}`); setFav(true); }
    } catch { toast.error("Error"); }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: event.title, url }); return; } catch {} }
    navigator.clipboard.writeText(url); toast.success(t("ev_copied"));
  };

  const boost = async () => {
    try {
      const res = await api.post(`/events/${slug}/promote/checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const adminDelete = async () => {
    if (!window.confirm(`Supprimer définitivement l'événement « ${event.title} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/events/${slug}`);
      toast.success("Événement supprimé");
      navigate("/events");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la suppression"); }
  };

  if (isLoading) return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!event) return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-stone-400">404</div>;

  const meta = CATEGORY_META[event.category] || {};
  const Icon = meta.icon;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Event",
    name: event.title, description: event.description,
    startDate: event.start_date, endDate: event.end_date,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: [event.image],
    location: { "@type": "Place", name: event.venue || event.city, address: { "@type": "PostalAddress", addressLocality: event.city, addressCountry: event.country } },
    organizer: { "@type": "Organization", name: event.organizer_name },
    ...(event.ticket_url ? { offers: { "@type": "Offer", url: event.ticket_url, availability: "https://schema.org/InStock" } } : {}),
  };
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Événements", item: `${origin}/events` },
      { "@type": "ListItem", position: 2, name: event.title, item: `${origin}/events/${slug}` },
    ],
  };

  return (
    <>
      <Seo title={event.title} description={event.description?.slice(0, 160)} image={event.nordic ? `${API}/events/${slug}/social-card.png` : event.image} jsonLd={[jsonLd, breadcrumbLd]} canonicalPath={`/events/${slug}`} ogType="article" />

      {/* HERO */}
      <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <EventImage src={event.image} alt={event.title} imgClassName="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0B0C0E 5%, rgba(11,12,14,0.4) 60%, rgba(11,12,14,0.6))" }} />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 w-full pb-10">
            <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-1.5 text-stone-300 hover:text-amber-400 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> {t("nav_events")}</button>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono tracking-wider uppercase bg-amber-950/70 backdrop-blur text-amber-300 border border-amber-800/40">
                {Icon && <Icon className="w-3.5 h-3.5" />} {tCategory(event.category)}
              </span>
              {event.distance_from_border_km === 0 ? (
                <span className="font-mono text-xs text-emerald-400/90 uppercase tracking-wider">{t("in_switzerland")}</span>
              ) : (
                <span className="font-mono text-xs text-stone-400 uppercase tracking-wider">~{event.distance_from_border_km} {t("ev_km_border")}</span>
              )}
              {event.sabbat && <SabbatBadge sabbat={event.sabbat} testid="event-detail-sabbat" />}
            </div>
            <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-100 text-glow leading-[1.02] max-w-4xl">{event.title}</motion.h1>
          </div>
        </div>
      </div>

      <CampfireTopBanner type="event" slug={slug} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 grid lg:grid-cols-[1fr_360px] gap-12">
        {/* MAIN */}
        <div>
          <div className="flex flex-wrap gap-3 mb-10">
            <button data-testid="favorite-btn" onClick={toggleFav} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm border transition-all ${fav ? "bg-amber-600 text-stone-950 border-amber-400" : "bg-[#181A20] text-stone-200 border-[#272A34] hover:border-amber-500/40"}`}>
              <Heart className={`w-4 h-4 ${fav ? "fill-current" : ""}`} /> {fav ? t("ev_favorited") : t("ev_favorite")}
            </button>
            <button data-testid="share-btn" onClick={share} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/40 transition-all"><Share2 className="w-4 h-4" /> {t("ev_share")}</button>
            <a data-testid="calendar-btn" href={buildGoogleCalUrl(event)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/40 transition-all"><CalendarPlus className="w-4 h-4" /> {t("ev_calendar")}</a>
            {user && (event.organizer_id === user.user_id || user.role === "admin") && (
              event.promoted ? (
                <span data-testid="promoted-indicator" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm bg-amber-950/50 text-amber-300 border border-amber-700/40"><Star className="w-4 h-4" /> {t("boosted")}</span>
              ) : (
                <button data-testid="boost-btn" onClick={boost} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-sm bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 text-stone-950 border border-amber-400/30 transition-all"><Star className="w-4 h-4" /> {t("boost_event")}</button>
              )
            )}
            {user?.role === "admin" && (
              <button data-testid="admin-delete-event-btn" onClick={adminDelete} title="Supprimer cet événement (admin)" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm bg-red-900/70 hover:bg-red-800 text-white border border-red-700/50 transition-all"><Trash2 className="w-4 h-4" /> Supprimer</button>
            )}
          </div>

          <div className="mb-8">
            <AttendanceButtons slug={slug} />
          </div>

          <EventPresence slug={slug} />

          <div className="prose prose-invert max-w-none">
            <p className="text-lg text-stone-300/90 leading-relaxed whitespace-pre-line">{event.description}</p>
          </div>

          {event.sabbat && (
            <div data-testid="event-detail-sabbat-note" className="mt-8 rounded-xl border border-indigo-800/40 bg-indigo-950/20 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-indigo-300" />
                <span className="font-serif text-xl text-indigo-100">{t("ev_sabbat_label")} · {t(`sabbat_${event.sabbat}`)}</span>
              </div>
              <p className="text-stone-300/90 leading-relaxed">{SABBAT_INFO[event.sabbat]?.history?.[lang]}</p>
              <p className="mt-3 text-sm text-indigo-200/80 leading-relaxed">{SABBAT_INFO[event.sabbat]?.ritual?.[lang]}</p>
              <Link to="/lune" data-testid="event-detail-sabbat-link" className="inline-flex items-center gap-1.5 mt-4 text-sm font-mono uppercase tracking-wider text-indigo-300 hover:text-indigo-200">
                {t("ev_sabbat_more")} <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}

          {event.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8">
              {event.tags.map((tag) => <span key={tag} className="px-3 py-1 rounded-full text-xs font-mono text-stone-400 bg-[#181A20] border border-[#272A34]">#{tag}</span>)}
            </div>
          )}

          {event.gallery?.length > 0 && (
            <div className="mt-12">
              <h3 className="font-serif text-xl text-amber-200/90 mb-4">{t("ev_gallery")}</h3>
              <div className="grid grid-cols-2 gap-4">
                {event.gallery.map((g, i) => <img key={i} src={g} alt="" className="rounded-lg border border-[#272A34] object-cover w-full h-48" />)}
              </div>
            </div>
          )}

          {/* MAP */}
          {event.lat != null && event.lng != null && (
          <div className="mt-12">
            <h3 className="font-serif text-xl text-amber-200/90 mb-4">{t("ev_where")}</h3>
            <div className="rounded-xl overflow-hidden border border-[#272A34]" style={{ height: "300px" }}>
              <MapContainer center={[event.lat, event.lng]} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer className="dark-tiles" url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                <Marker position={[event.lat, event.lng]} icon={pin} />
              </MapContainer>
            </div>
          </div>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
          {event.nordic && (
            <div className="bg-gradient-to-br from-indigo-950/50 to-[#121418] border border-indigo-800/40 rounded-xl p-6" data-testid="event-countdown-block">
              <div className="font-mono text-[11px] uppercase tracking-wider text-indigo-300/80 mb-4 flex items-center gap-2"><Timer className="w-4 h-4" /> {t("ev_countdown")}</div>
              <Countdown target={event.start_date} />
            </div>
          )}
          <div className="bg-[#121418] border border-[#272A34] rounded-xl p-6 space-y-5">
            <InfoRow icon={CalendarDays} label={t("ev_when")}>
              <div className="text-stone-200">{formatDateRange(event.start_date, event.end_date, lang)}</div>
              <div className="text-xs text-stone-500 font-mono flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {formatTime(event.start_date, lang)}</div>
            </InfoRow>
            <InfoRow icon={MapPin} label={t("ev_where")}>
              <div className="text-stone-200">{event.venue}</div>
              <div className="text-sm text-stone-400">{event.city}, {tCountry(event.country)}{event.canton ? <> · <Link to={`/canton/${event.canton}`} className="text-amber-400 hover:text-amber-300 underline" data-testid="canton-link">{event.canton}</Link></> : ""}</div>
            </InfoRow>
            <InfoRow icon={User2} label={t("ev_organizer")}>
              {event.organizer_id ? (
                <Link to={`/u/${event.organizer_id}`} data-testid="organizer-profile-link" className="text-amber-400 hover:text-amber-300 underline">{event.organizer_name}</Link>
              ) : (
                <div className="text-stone-200">{event.organizer_name}</div>
              )}
            </InfoRow>
            {event.price_info && <InfoRow icon={Ticket} label={t("ev_price")}><div className="text-stone-200">{event.price_info}</div></InfoRow>}

            {event.ticket_url && (
              <a data-testid="ticket-link" href={event.ticket_url} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-6 py-3 rounded-md shadow-lg transition-all border border-amber-400/30">
                <Ticket className="w-4 h-4" /> {t("ev_tickets")}
              </a>
            )}
            {event.official_url && (
              <a data-testid="official-link" href={event.official_url} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/40 px-6 py-3 rounded-md transition-all"><ExternalLink className="w-4 h-4" /> {t("ev_website")}{event.official_source_name ? ` · ${event.official_source_name}` : ""}</a>
            )}
            {!event.official_url && event.external_url && (
              <a data-testid="website-link" href={event.external_url} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/40 px-6 py-3 rounded-md transition-all"><ExternalLink className="w-4 h-4" /> {t("ev_website")}</a>
            )}
          </div>
          {event.nordic && <ConcertShare event={event} />}
          <div className="flex justify-end pt-1">
            <ReportButton targetType="event" targetSlug={slug} />
          </div>
        </aside>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
        <EventDiscussion slug={slug} />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-8">
        <FeaturedPros placement="event" limit={3} />
      </div>

      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-16">
          <h2 className="font-serif text-2xl sm:text-3xl text-stone-100 border-b border-amber-900/30 pb-4 mb-8">{t("ev_related")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{related.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}</div>
        </section>
      )}

      <CampfireRelated type="event" slug={slug} />
    </>
  );
}

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex gap-3">
    <Icon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <div className="font-mono text-[11px] uppercase tracking-wider text-stone-500 mb-0.5">{label}</div>
      {children}
    </div>
  </div>
);
